import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  adminDeleteKill,
  adminDeleteMission,
  adminGetKills,
  adminGetMission,
  adminToggleHidden,
  adminUpdateKill,
  adminUpdateMission,
} from '@/lib/api'
import { PageMotion } from '@/components/Motion'
import { ErrorBlock, LoadingBlock, PageHeader, Panel } from '@/components/ui'

export default function AdminMissionEditor() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const [killPage, setKillPage] = useState(1)
  const [form, setForm] = useState({
    missionName: '',
    worldname: '',
    missionAuthor: '',
    missionType: '',
    victory: '',
    missionStart: '',
    missionEnd: '',
    scoreBlue: '',
    scoreRed: '',
    scoreGreen: '',
    ocapLink: '',
    notes: '',
  })

  const mission = useQuery({
    queryKey: ['admin-mission', id],
    queryFn: () => adminGetMission(id),
    enabled: !!id,
  })

  const kills = useQuery({
    queryKey: ['admin-kills', id, killPage],
    queryFn: () => adminGetKills(id, killPage, 50),
    enabled: !!id,
  })

  useEffect(() => {
    if (!mission.data) return
    const m = mission.data
    setForm({
      missionName: m.missionName || '',
      worldname: m.worldname || '',
      missionAuthor: m.missionAuthor || '',
      missionType: m.missionType || '',
      victory: m.victory || '',
      missionStart: m.missionStart || '',
      missionEnd: m.missionEnd || '',
      scoreBlue: m.scoreBlue || '',
      scoreRed: m.scoreRed || '',
      scoreGreen: m.scoreGreen || '',
      ocapLink: m.ocapLink || '',
      notes: m.notes || '',
    })
  }, [mission.data])

  const save = useMutation({
    mutationFn: () => adminUpdateMission(id, form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-mission', id] }),
  })

  const toggle = useMutation({
    mutationFn: () => adminToggleHidden(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-mission', id] }),
  })

  const delMission = useMutation({
    mutationFn: () => adminDeleteMission(id),
  })

  const delKill = useMutation({
    mutationFn: (kid: number) => adminDeleteKill(kid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-kills', id] }),
  })

  if (mission.isLoading) return <LoadingBlock />
  if (mission.error || !mission.data) return <ErrorBlock message="Mission not found" />

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }))

  return (
    <PageMotion className="space-y-8">
      <PageHeader
        breadcrumb={
          <p className="text-xs uppercase tracking-[0.14em] text-ink-faint">
            <Link to="/admin/missions" className="hover:text-accent-text transition-colors">
              Missions
            </Link>
            <span className="mx-2">/</span>#{id}
          </p>
        }
        title={form.missionName || `Mission #${id}`}
        action={
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-ghost" onClick={() => toggle.mutate()}>
              {mission.data.hidden ? 'Unhide' : 'Hide'}
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => {
                if (confirm('Delete this mission permanently?')) {
                  delMission.mutate(undefined, {
                    onSuccess: () => {
                      window.location.href = '/admin/missions'
                    },
                  })
                }
              }}
            >
              Delete
            </button>
          </div>
        }
      />

      <Panel title="Metadata">
        <div className="grid gap-3 sm:grid-cols-2">
          {(
            [
              ['missionName', 'Name'],
              ['worldname', 'Map'],
              ['missionAuthor', 'Author'],
              ['missionType', 'Type'],
              ['victory', 'Victory'],
              ['missionStart', 'Start'],
              ['missionEnd', 'End'],
              ['scoreBlue', 'Score blue'],
              ['scoreRed', 'Score red'],
              ['scoreGreen', 'Score green'],
              ['ocapLink', 'OCAP link'],
            ] as const
          ).map(([key, label]) => (
            <div key={key}>
              <label className="label">{label}</label>
              <input className="input" value={form[key]} onChange={set(key)} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="label">Notes</label>
            <textarea className="input min-h-[80px]" value={form.notes} onChange={set('notes')} />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button type="button" className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
          {save.isSuccess ? <span className="text-xs text-accent-text">Saved</span> : null}
        </div>
      </Panel>

      <Panel title="Kills" flush>
        {kills.isLoading ? (
          <LoadingBlock />
        ) : (
          <>
            <div className="table-wrap border-0 rounded-none">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Killer</th>
                    <th>Weapon</th>
                    <th>Victim</th>
                    <th>Dist</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(kills.data?.data ?? []).map(
                    (k: {
                      id: number
                      time: string
                      killerName: string
                      victimName: string
                      weapon: string
                      distance: string
                      killerId: number
                      victimId: number
                    }) => (
                      <KillRow
                        key={k.id}
                        kill={k}
                        onSave={(body) =>
                          adminUpdateKill(k.id, body).then(() =>
                            qc.invalidateQueries({ queryKey: ['admin-kills', id] }),
                          )
                        }
                        onDelete={() => {
                          if (confirm('Delete kill?')) delKill.mutate(k.id)
                        }}
                      />
                    ),
                  )}
                </tbody>
              </table>
            </div>
            {kills.data && kills.data.totalPages > 1 && (
              <div className="flex justify-between px-4 py-3 border-t border-line text-sm">
                <span className="text-ink-faint">
                  Page {kills.data.page} / {kills.data.totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={killPage <= 1}
                    onClick={() => setKillPage((p) => p - 1)}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={killPage >= kills.data.totalPages}
                    onClick={() => setKillPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Panel>
    </PageMotion>
  )
}

function KillRow({
  kill,
  onSave,
  onDelete,
}: {
  kill: {
    id: number
    time: string
    killerName: string
    victimName: string
    weapon: string
    distance: string
    killerId: number
    victimId: number
  }
  onSave: (body: Record<string, unknown>) => Promise<unknown>
  onDelete: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [time, setTime] = useState(kill.time)
  const [weapon, setWeapon] = useState(kill.weapon)
  const [distance, setDistance] = useState(kill.distance)
  const [saving, setSaving] = useState(false)

  if (!editing) {
    return (
      <tr>
        <td className="font-mono text-xs text-ink-faint">{kill.time}</td>
        <td>{kill.killerName}</td>
        <td className="text-xs text-ink-muted">{kill.weapon}</td>
        <td>{kill.victimName}</td>
        <td className="font-mono text-xs">{kill.distance}</td>
        <td>
          <div className="flex gap-1 justify-end">
            <button type="button" className="btn-ghost !py-1 !px-2 text-xs" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button type="button" className="btn-ghost !py-1 !px-2 text-xs text-rose-400" onClick={onDelete}>
              Del
            </button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td>
        <input className="input !py-1 !text-xs" value={time} onChange={(e) => setTime(e.target.value)} />
      </td>
      <td className="text-xs text-ink-faint">{kill.killerName}</td>
      <td>
        <input className="input !py-1 !text-xs" value={weapon} onChange={(e) => setWeapon(e.target.value)} />
      </td>
      <td className="text-xs text-ink-faint">{kill.victimName}</td>
      <td>
        <input
          className="input !py-1 !text-xs w-20"
          value={distance}
          onChange={(e) => setDistance(e.target.value)}
        />
      </td>
      <td>
        <div className="flex gap-1 justify-end">
          <button
            type="button"
            className="btn-primary !py-1 !px-2 text-xs"
            disabled={saving}
            onClick={async () => {
              setSaving(true)
              try {
                await onSave({ time, weapon, distance, killerId: kill.killerId, victimId: kill.victimId })
                setEditing(false)
              } finally {
                setSaving(false)
              }
            }}
          >
            Save
          </button>
          <button type="button" className="btn-ghost !py-1 !px-2 text-xs" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </td>
    </tr>
  )
}
